import React from "react";
import { Check } from "lucide-react";
import cx from "classnames";
import { getFormattedPrice } from "../../utils/creditUtils";
import { JhButton } from "designPackages/buttonPackage";
import { JhText } from "designPackages/textPackage";
import PlanStepper from "../PremiumPlanCards/PlanStepper";

interface PricingCardProps {
  item: any;
  isRecommended: boolean;
  hasDiscount: boolean;
  isSaleLive: boolean;
  features: string[];
  featureTitle: string;
  onBuyClick: (item: any) => void;
}

const PricingCard: React.FC<PricingCardProps> = ({
  item,
  isRecommended,
  hasDiscount,
  isSaleLive,
  features,
  featureTitle,
  onBuyClick
}) => {
  return (
    <div
      className={cx(
        "relative flex col-span-1 flex-col justify-center gap-5 py-8 px-4 w-full bg-card border rounded-lg",
        isRecommended && !isSaleLive
          ? "mt-10 md:mt-0 border-4 border-[#2563EB33] shadow-[0px_26px_51.4px_rgba(85,141,255,0.1),inset_0_0_0_1px_rgb(37,99,235)]"
          : "border border-border"
      )}
    >
      {/* Recommended Badge */}
      {isRecommended && !isSaleLive && (
        <div className="absolute top-[-40px] min-h-9 left-0 right-0 flex flex-row justify-center items-center bg-accent rounded-t-xl">
          <JhText variant="body2" color="primary">
            Recommended
          </JhText>
        </div>
      )}

      {/* Price Section */}
      <div className="flex flex-col w-full gap-1.5">
        <div className="flex flex-row items-baseline gap-1.5">
          <JhText variant="title6" color="foreground">
            ₹ {getFormattedPrice(item.discountedPrice)}
          </JhText>
          {hasDiscount && (
            <JhText variant="body2" color="muted3" className="line-through">
              ₹ {getFormattedPrice(item.maximumRetailPrice)}
            </JhText>
          )}
        </div>
        <JhText variant="body2" color="muted-foreground">
          Suitable for {item.noOfJobs || 1} premium job
          {item.noOfJobs > 1 ? "s" : ""}
        </JhText>
      </div>

      {/* Credits Section */}
      <div className="flex flex-row items-center justify-between gap-2.5 py-3 w-full border-t border-b border-border">
        <div className="flex flex-row items-center gap-1 h-9">
          <img
            src="/hire/plan-selection-credit-coin.svg"
            alt="coin"
            width={24}
            height={24}
          />
          <JhText variant="subtitle2" color="foreground">
            {getFormattedPrice(item.credits)} credits
          </JhText>
        </div>
        {hasDiscount && (
          <div className="flex flex-row justify-center items-center gap-2.5 p-1.5 bg-success border border-brand-muted rounded-md">
            <JhText variant="caption1" color="success-foreground">
              Save {item.discountPercentage}%
            </JhText>
          </div>
        )}
      </div>

      {/* Features Section */}
      <div className="flex flex-col gap-2.5 w-full">
        <JhText variant="subtitle4" color="foreground">
          {featureTitle}
        </JhText>
        <div className="flex flex-col gap-1.5 w-full">
          {features.map((feature, index) => (
            <div key={index} className="flex flex-row items-center gap-1.5">
              <Check
                size={16}
                className="text-muted-foreground flex-shrink-0"
              />
              <JhText variant="caption2" color="muted-foreground">
                {feature}
              </JhText>
            </div>
          ))}
        </div>
      </div>

      {/* Quantity Stepper */}
      {item.maxQuantityAllowed > 1 && (
        <div>
          <PlanStepper maxQuantityAllowed={item.maxQuantityAllowed} />
        </div>
      )}

      {/* CTA Button */}
      <div className="flex flex-row justify-center items-center gap-1 py-3 w-full">
        <JhButton
          variant={isRecommended ? "default" : "outline"}
          size="default"
          className="w-full"
          onClick={() => onBuyClick(item)}
        >
          Buy now
        </JhButton>
      </div>
    </div>
  );
};

export default PricingCard;
